"use client";

import { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import {
  clearRememberedEmail,
  readRememberedEmail,
  saveRememberedEmail,
} from "@/lib/auth/remembered-email";
import { broadcast } from "@/lib/tab-sync/channel";
import {
  signInWithEmail,
  signInWithGoogle,
  signInWithApple,
  signOutFromFirebase,
  type FirebaseSignInResult,
} from "@/lib/auth/firebase";
// loginPersisted is emitted server-side from /api/auth/login (T054) with a
// trustworthy user-agent. Do NOT also emit it client-side here.

// Date.now() reads outside React's purity boundary; pulled out so the
// linter doesn't flag it inside the component body.
const nowMs = (): number => Date.now();

const FIREBASE_CONFIG_KEYS = [
  "NEXT_PUBLIC_FIREBASE_API_KEY",
  "NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN",
  "NEXT_PUBLIC_FIREBASE_PROJECT_ID",
  "NEXT_PUBLIC_FIREBASE_APP_ID",
] as const;

function checkFirebaseConfig(): string[] {
  const env = {
    NEXT_PUBLIC_FIREBASE_API_KEY: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
    NEXT_PUBLIC_FIREBASE_PROJECT_ID: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    NEXT_PUBLIC_FIREBASE_APP_ID: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  };
  return FIREBASE_CONFIG_KEYS.filter((k) => !env[k]);
}

const FormSchema = z.object({
  email: z
    .string({ message: "請輸入 Email" })
    .min(1, "請輸入 Email")
    .email("Email 格式不正確"),
  password: z
    .string({ message: "請輸入密碼" })
    .min(1, "請輸入密碼"),
});

type FormValues = z.infer<typeof FormSchema>;

export function LoginForm({ next }: { next?: string }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const reason = searchParams.get("reason");
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [missingEnvKeys, setMissingEnvKeys] = useState<string[]>([]);
  const [rememberMe, setRememberMe] = useState(true);

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { email: "", password: "" },
  });

  useEffect(() => {
    // Defer state set to a microtask to satisfy react-hooks/set-state-in-effect.
    Promise.resolve().then(() => {
      const missing = checkFirebaseConfig();
      if (missing.length > 0) {
        setMissingEnvKeys(missing);
        console.error(
          `[md3-adm] Missing Firebase env keys: ${missing.join(", ")}. ` +
            "See .env.example. Restart dev server after editing .env.local.",
        );
      }
      const saved = readRememberedEmail();
      if (saved) form.setValue("email", saved);
    });
    // form is stable from useForm() — safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Shared BFF exchange + redirect path for all three sign-in methods.
   * Takes a Firebase result and finishes the login dance:
   *   - cancelled  → reset to idle, no error
   *   - error      → show message
   *   - ok         → POST {email, idToken} to BFF; on non-OK clear
   *                  Firebase user (so retry doesn't hit a stuck state)
   */
  const completeSignIn = async (fb: FirebaseSignInResult) => {
    if (fb.kind === "cancelled") {
      setSubmitting(false);
      return;
    }
    if (fb.kind === "error") {
      setServerError(fb.message);
      setSubmitting(false);
      return;
    }
    // Remember the canonical Firebase-returned email (not the form's
    // — Google/Apple may give us a different address) BEFORE the BFF
    // call, so a refresh after a BFF failure still has the prefill.
    if (rememberMe) {
      saveRememberedEmail(fb.email);
    } else {
      clearRememberedEmail();
    }
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: fb.email, idToken: fb.idToken, next }),
        cache: "no-store",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ message: "登入失敗" }));
        setServerError(body.message ?? "登入失敗");
        // BFF rejected — sign out from Firebase so the next click doesn't
        // succeed at Firebase but fail again at BFF in a confusing loop.
        await signOutFromFirebase();
        setSubmitting(false);
        return;
      }
      const data = (await res.json()) as { user: { id: string }; redirectTo: string };
      broadcast({ type: "login", at: nowMs() });
      // Sidebar permission hydration is handled by RbacProvider, which
      // watches usePathname() and re-fetches /api/auth/me when navigating
      // away from /login. Do NOT call useRbac().refresh() here — the
      // automatic trigger covers every login entry point (password,
      // future SSO, etc.) without each one having to remember.
      // See spec: adm-sidebar-permission-hydration.
      router.push(data.redirectTo);
      router.refresh();
    } catch (err) {
      console.error("[LoginForm] BFF exchange failed:", err);
      setServerError(err instanceof Error ? err.message : "登入時發生未預期錯誤");
      await signOutFromFirebase();
      setSubmitting(false);
    }
  };

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    setSubmitting(true);
    try {
      const fb = await signInWithEmail(values.email, values.password);
      await completeSignIn(fb);
    } catch (err) {
      console.error("[LoginForm] onSubmit failed:", err);
      setServerError(err instanceof Error ? err.message : "登入時發生未預期錯誤");
      setSubmitting(false);
    }
  };

  const onClickGoogle = async () => {
    setServerError(null);
    setSubmitting(true);
    try {
      const fb = await signInWithGoogle();
      await completeSignIn(fb);
    } catch (err) {
      console.error("[LoginForm] Google sign-in failed:", err);
      setServerError(err instanceof Error ? err.message : "Google 登入失敗");
      setSubmitting(false);
    }
  };

  const onClickApple = async () => {
    setServerError(null);
    setSubmitting(true);
    try {
      const fb = await signInWithApple();
      await completeSignIn(fb);
    } catch (err) {
      console.error("[LoginForm] Apple sign-in failed:", err);
      setServerError(err instanceof Error ? err.message : "Apple 登入失敗");
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      // Defense-in-depth: pre-hydration Enter key would otherwise GET-submit
      // and put the password in the URL. method="post" prevents that even
      // though the actual submission is intercepted by JS once hydrated.
      // action points at the BFF's traditional URL (no longer the primary
      // path; JS does Firebase first now).
      method="post"
      action="/api/auth/login"
      className="space-y-4"
      noValidate
    >
      {reason === "session-expired" && (
        <Alert variant="default" className="border-amber-200 bg-amber-50 text-amber-900">
          <AlertDescription>登入逾時，請重新登入</AlertDescription>
        </Alert>
      )}

      {missingEnvKeys.length > 0 && (
        <Alert variant="default" className="border-red-200 bg-red-50 text-red-900">
          <AlertDescription>
            <p className="font-medium">Firebase 設定缺失，無法登入</p>
            <p className="mt-1 text-xs">
              .env.local 缺少：{missingEnvKeys.join(", ")}
              。補上後請重啟 dev server。
            </p>
          </AlertDescription>
        </Alert>
      )}

      {serverError && (
        <Alert variant="default" className="border-red-200 bg-red-50 text-red-900">
          <AlertDescription>{serverError}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          autoComplete="username email"
          autoFocus
          disabled={submitting}
          {...form.register("email")}
        />
        {form.formState.errors.email && (
          <p className="text-xs text-red-600">{form.formState.errors.email.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">密碼</Label>
        <Input
          id="password"
          type="password"
          autoComplete="current-password"
          disabled={submitting}
          {...form.register("password")}
        />
        {form.formState.errors.password && (
          <p className="text-xs text-red-600">{form.formState.errors.password.message}</p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id="rememberMe"
          checked={rememberMe}
          onCheckedChange={(v) => setRememberMe(v === true)}
          disabled={submitting}
        />
        <Label
          htmlFor="rememberMe"
          className="cursor-pointer text-sm font-normal text-muted-foreground"
        >
          記住 Email
        </Label>
      </div>

      <Button
        type="submit"
        // Disable when env is broken — clicking would silently fail.
        // Pre-hydration native submit is handled by the BFF (redirects
        // to /login?reason=js-required); no client-side gate needed.
        disabled={submitting || missingEnvKeys.length > 0}
        className="w-full"
      >
        {submitting ? "登入中…" : "登入"}
      </Button>

      <div className="relative my-2">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-background px-2 text-muted-foreground">或</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClickGoogle}
          disabled={submitting || missingEnvKeys.length > 0}
        >
          <GoogleIcon />
          <span>Google</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onClickApple}
          disabled={submitting || missingEnvKeys.length > 0}
        >
          <AppleIcon />
          <span>Apple</span>
        </Button>
      </div>
    </form>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
      />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden>
      <path d="M16.365 1.43c0 1.14-.466 2.236-1.215 3.038-.804.86-2.114 1.523-3.198 1.439-.13-1.115.444-2.27 1.176-3.045.819-.864 2.226-1.51 3.237-1.432zM21 17.36c-.612 1.41-.91 2.04-1.7 3.288-1.106 1.745-2.665 3.916-4.6 3.93-1.72.013-2.163-1.117-4.498-1.105-2.336.013-2.823 1.123-4.543 1.105-1.935-.014-3.412-1.971-4.519-3.716C-1.93 16.5-2.255 11.067-.345 8.255 1.05 6.232 3.213 5 5.255 5c2.078 0 3.385 1.116 5.103 1.116 1.667 0 2.683-1.118 5.085-1.118 1.815 0 3.74 0.987 5.107 2.69-4.487 2.46-3.756 8.866.45 9.671z" />
    </svg>
  );
}
